package com.macrofocus.eflux

import com.macrofocus.common.crossplatform.Timer
import com.macrofocus.common.properties.*
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.selection.SelectionEvent
import com.macrofocus.common.selection.SelectionListener
import com.macrofocus.common.units.Kilovolt
import com.macrofocus.eflux.animation.Easing
import com.macrofocus.eflux.animation.Playback
import com.macrofocus.eflux.animation.timeline
import com.macrofocus.eflux.data.quest.loadCoordinates
import com.macrofocus.eflux.data.quest.loadITCM
import com.macrofocus.eflux.data.quest.loadPhysicalNetworkFromDatabase
import com.macrofocus.eflux.data.raw.RawPowerFlowParser
import com.macrofocus.eflux.diagram.DiagramType
import com.macrofocus.eflux.network.*
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.logical.*
import com.macrofocus.eflux.network.physical.PhysicalNetwork
import com.macrofocus.eflux.network.risk.RiskComponent
import com.macrofocus.eflux.network.schema.Schema
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.mui.utilities.isChrome
import com.macrofocus.eflux.ui.updateDiagram
import com.macrofocus.eflux.ui.zoomFactorInterpolate
import deckgl.util.calculateZoomLevel
import korlibs.time.DateTime
import kotlinx.browser.window
import kotlinx.coroutines.*
import mui.material.styles.Theme
import org.locationtech.jts.geom.Envelope
import org.locationtech.jts.geom.Geometry
import org.mkui.color.MkColor
import org.mkui.color.colorOf
import org.mkui.color.toKotlinJS
import org.molap.dataframe.DataFrame
import org.molap.json.readJson
import org.w3c.xhr.XMLHttpRequest
import svgpanzoom.SvgPanZoomContext
import web.cssom.Color
import web.events.EventType
import web.events.addEventListener
import web.sse.EventSource
import kotlin.coroutines.*
import kotlin.js.Date
import kotlin.js.Promise
import kotlin.math.absoluteValue
import kotlin.time.ExperimentalTime
import kotlin.time.measureTime

fun launch(block: suspend () -> Unit) {
    block.startCoroutine(object : Continuation<Unit> {
        override val context = EmptyCoroutineContext
        override fun resumeWith(result: Result<Unit>) = Unit
    })
}

suspend fun delay(ms: Long): Unit = suspendCoroutine { continuation ->
    setTimeout({
        continuation.resume(Unit)
    }, ms)
}

suspend fun cancel(): Unit = suspendCoroutine { continuation ->
    continuation.context.cancel()
}

//Use top level setTimeout function instead of window.setTimeout to use with node.js
external fun setTimeout(function: () -> Unit, delay: Long)

/**
 *
 */
class DataLoader(val earliest: Promise<String>, val latest: Promise<String>, val diagramTypeProperty: SimpleProperty<DiagramType>, val configuration: EFluxConfiguration) {
    val exportMode = configuration.expertMode

    val lag: MutableProperty<Int> = configuration.lag
    val lagUnit: MutableProperty<LagUnit> = configuration.lagUnit
    val masterDateTime: MutableProperty<DateTime> = configuration.masterDateTime
    val live: MutableProperty<Boolean> = configuration.live

    val physicalNetwork: MutableProperty<PhysicalNetwork?> = configuration.physicalNetwork
    val logicalNetwork: MutableProperty<LogicalNetwork?> = configuration.logicalNetwork
    val substationNetwork = MutableProperty.of<SubstationNetwork?>(null)
    val coordinatesDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = configuration.coordinatesDataFrame
    var itcmDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = configuration.itcmDataFrame
    var amicaDataFrame: MutableProperty<DataFrame<Int, String, Any?>?> = configuration.amicaDataFrame

    val sizeNodes: MutableProperty<NodeField<*>?> = configuration.sizeNodes
    val colorNodes: MutableProperty<NodeField<*>?> = configuration.colorNodes

    val tooltipNodes: MutableProperty<Array<NodeField<*>>> = configuration.tooltipNodes
    val tooltipLinks: MutableProperty<Array<LinkField<*>>> = configuration.tooltipLinks

    val heatmapNodes: MutableProperty<NodeField<*>?> = configuration.heatmapNodes

    val thicknessLinks: MutableProperty<LinkField<*>?> = configuration.thicknessLinks
    val colorLinks: MutableProperty<LinkField<*>?> = configuration.colorLinks
    val movementLinks: MutableProperty<LinkField<*>?> = configuration.movementLinks
    val animationLinks: MutableProperty<Boolean> = configuration.animationLinks
    val heatmapLinks: MutableProperty<LinkField<*>?> = configuration.heatmapLinks

    val cities: MutableProperty<Boolean> = configuration.cities
    val labelSubstations: MutableProperty<Boolean> = configuration.labelSubstations
    val border: MutableProperty<Boolean> = configuration.border
    val legend: MutableProperty<Boolean> = configuration.legend
    val dnd: MutableProperty<Boolean> = configuration.dnd
    val tooltips: MutableProperty<Boolean> = configuration.tooltips
    val lod: MutableProperty<Boolean> = configuration.lod
    val generators: MutableProperty<Boolean> = configuration.generators
    val lightning: MutableProperty<Boolean> = configuration.lightning
    val weather: MutableProperty<Boolean> = configuration.weather
    val weatherVariable: MutableProperty<String> = configuration.weatherVariable
    val terrain: MutableProperty<Boolean> = configuration.terrain

    val schemas: MutableProperty<Boolean> = SimpleProperty(false)

    val download: MutableProperty<Boolean> = configuration.download

    // 3D
//    val threeDPosition: MutableProperty<ThreeDPosition> = SimpleProperty(ThreeDPosition(7.969664962869274, 47.021278030856564, 7.0, 60.0, 0.0))
    val envelope = Envelope(5.9559, 10.4921, 45.8179, 47.8085)
    val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight, 0.07)
    val threeDZoom: MutableProperty<Double> = SimpleProperty(zoom) // 7.0
    val threeDLatitude: MutableProperty<Double> = SimpleProperty(envelope.centre()!!.y) // 46.801111, 47.021278030856564
    val threeDLongitude: MutableProperty<Double> = SimpleProperty(envelope.centre()!!.x) // 8.226667. 7.969664962869274
    val threeDPitch: MutableProperty<Double> = SimpleProperty(0.0) // 60.0
    val threeDBearing: MutableProperty<Double> = SimpleProperty(0.0)
    val threeDArc: MutableProperty<Boolean> = SimpleProperty(false)
    val threeDViewState: MutableProperty<dynamic> = SimpleProperty(null)
    val threeDOnLoad: MutableProperty<Boolean?> = SimpleProperty(null)
    val geoNetwork: MutableProperty<GeoNetwork?> = SimpleProperty(null)

    val zoomLevelLinear = MutableProperty.of(0.0)
    val minZoomLevel = MutableProperty.of(5.0)
    val maxZoomLevel = MutableProperty.of(20.0)
    val autoZoom = MutableProperty.of(true)
    val autoTerrain = MutableProperty.of(true)
    val schematic = MutableProperty.of("eflux")
    val cartographic = MutableProperty.of(0.0)
    val autoMorphing = MutableProperty.of(true)
    val startZoomLevelMorphing = MutableProperty.of(9.0)
    val endZoomLevelMorphing = MutableProperty.of(11.0)
    val lineWidth = MutableProperty.of(7.0)
    val fps = MutableProperty.of(30.0)
    val riskBuffer = MutableProperty.of(5.0)
    val riskThreshold = MutableProperty.of(5.0)

    val riskMask = SimpleProperty(0b1100110111)

    val controller: EFluxController = SingleEFluxController(configuration)
    val logicalBusProbing: MutableSingleSelection<LogicalBusId> = controller.primaryLogicalBusProbing
    val logicalBranchProbing: MutableSingleSelection<LogicalBranchId> = controller.primaryLogicalBranchProbing
    val riskComponentProbing: MutableSingleSelection<RiskComponent> = controller.primaryRiskComponentProbing
    val coordinateProbing: MutableSingleSelection<Coordinate> = controller.primaryCoordinateProbing
    val schemaProbing: MutableSingleSelection<Schema> = controller.primarySchemaProbing

    val logicalBusSelection: MutableSelection<LogicalBusId> = controller.primaryLogicalBusSelection
    val logicalBranchSelection: MutableSelection<LogicalBranchId> = controller.primaryLogicalBranchSelection
    val riskComponentSelection: MutableSelection<RiskComponent> = controller.primaryRiskComponentSelection
    val coordinateSelection: MutableSelection<Coordinate> = controller.primaryCoordinateSelection
    val schemaSelection: MutableSelection<Schema> = controller.primarySchemaSelection

    val fullName = MutableProperty.of(true)

    val parameters: Map<String, String?>

    val diagramLoaders: Array<ViewLoader>

    var playback: MutableProperty<Playback?> = SimpleProperty(null)

    init {
        playback.addPropertyListener(object : PropertyListener<Playback?> {
            override fun propertyChanged(event: PropertyEvent<Playback?>) {
                println("Cancelling playback: ${event.oldValue} and starting new one: ${event.newValue}")
                event.oldValue?.timeline?.stop()
                event.newValue?.timeline?.start()
            }
        })

        configuration.logicalBusSelection.addSelectionListener(object : SelectionListener<LogicalBusId> {
            override fun selectionChanged(event: SelectionEvent<LogicalBusId>) {
                println("Substation selection: ${event}")
            }
        })

        configuration.logicalBranchSelection.addSelectionListener(object : SelectionListener<LogicalBranchId> {
            override fun selectionChanged(event: SelectionEvent<LogicalBranchId>) {
                println("Link selection: ${event}")
            }
        })

        // Get parameters from request.
        val url = window.location.href
        val parametersPart = url.substringAfter("?").split('&')
        parameters = mutableMapOf(
            "expert" to null as String?,
            "date" to "latest" as String?,
            "live" to null as String?,
            "select" to null as String?, // "SLAUFE1"
            "localities" to null as String?,
            "border" to null as String?,
            "legend" to null as String?,
            "tooltips" to null as String?,
            "dnd" to isChrome.toString() as String?,
            "tooltips" to null as String?,
            "lod" to null as String?,
            "generators" to null as String?,
            "download" to null as String?,
            "animate" to null as String?,
            "sizeNodes" to null as String?,
            "colorNodes" to null as String?,
            "backgroundNodes" to null as String?,
            "tooltipNodes" to null as String?,
            "thicknessLinks" to null as String?,
            "colorLinks" to null as String?,
            "movementLinks" to null as String?,
            "animationLinks" to null as String?,
            "backgroundLinks" to null as String?,
            "tooltipLinks" to null as String?,
            "diagram" to null as String?,
            "weather" to null as String?,
            "weatherVariable" to "t_70m",
            "tableType" to null as String?,
            "crossBorder" to null as String?,
            "zoneFilter" to null as String?,
            "filter" to null as String?,
            "zoomLevelLinear" to null as String?,
            "minZoomLevel" to null as String?,
            "maxZoomLevel" to null as String?,
            "autoZoom" to null as String?,
            "autoTerrain" to null as String?,
            "schematic" to null as String?,
            "cartographic" to null as String?,
            "autoMorphing" to null as String?,
            "startZoomLevelMorphing" to null as String?,
            "endZoomLevelMorphing" to null as String?,
            "lineWidth" to null as String?,
            "fps" to null as String?,
            "riskBuffer" to null as String?,
            "riskThreshold" to null as String?
        )
        parametersPart.forEach {
            val parsedParameter = it.split("=", limit = 2)
            if (parsedParameter.size == 2) {
                val (key, value) = parsedParameter
                parameters[key] = value
            } else if(parsedParameter.size == 1) {
                // If there is no equal sign, treat as boolean and turn it on by default
                val key = parsedParameter.first()
                parameters[key] = "true"
            }
        }

        parameters["expert"]?.let { exportMode.value = it.toBoolean() }
        // Disable live sync if an explicit date is provided
        parameters["date"]?.let { if(it != "latest") live.value = false }
        parameters["live"]?.let { live.value = it.toBoolean() }
        parameters["localities"]?.let { labelSubstations.value = it.toBoolean() }
        parameters["border"]?.let { border.value = it.toBoolean() }
        parameters["legend"]?.let { legend.value = it.toBoolean() }
        parameters["dnd"]?.let { dnd.value = it.toBoolean() }
        parameters["tooltips"]?.let { tooltips.value = it.toBoolean() }
        parameters["lod"]?.let { lod.value = it.toBoolean() }
        parameters["generators"]?.let { generators.value = it.toBoolean() }
        parameters["download"]?.let { download.value = it.toBoolean() }
        parameters["sizeNodes"]?.let { value ->
            sizeNodes.value = NodeField.all.find { it.longName == value || it.name == value }
        }
        parameters["colorNodes"]?.let { value ->
            colorNodes.value = NodeField.all.find { it.longName == value || it.name == value }
        }
        parameters["backgroundNodes"]?.let { value ->
            heatmapNodes.value = NodeField.heatmap.find { it.longName == value || it.name == value }
        }
        parameters["tooltipNodes"]?.let { value ->
            tooltipNodes.value = NodeField.all.filter { it.longName == value || it.name == value }.toTypedArray()
        }
        parameters["thicknessLinks"]?.let { value ->
            thicknessLinks.value = LinkField.all.find { it.longName == value || it.name == value }
        }
        parameters["colorLinks"]?.let { value ->
            colorLinks.value = LinkField.all.find { it.longName == value || it.name == value }
        }
        parameters["movementLinks"]?.let { value ->
            movementLinks.value = LinkField.all.find { it.longName == value || it.name == value }
        }
        parameters["animationLinks"]?.let { animationLinks.value = it.toBoolean() }
        parameters["backgroundLinks"]?.let { value ->
            heatmapLinks.value = LinkField.heatmap.find { it.longName == value || it.name == value }
        }
        parameters["tooltipLinks"]?.let { value ->
            tooltipLinks.value = LinkField.all.filter { it.longName == value || it.name == value }.toTypedArray()
        }
        parameters["diagram"]?.let { value ->
            diagramTypeProperty.value = when (value) {
                "geo" -> DiagramType.Geo
                "schematic" -> DiagramType.Schematic
                "3d" -> DiagramType.ThreeD
                "table" -> DiagramType.Table
                else -> DiagramType.Tube
            }
        }
        parameters["weather"]?.let { weather.value = it.toBoolean() }
        parameters["weatherVariable"]?.let { weatherVariable.value = it }
        parameters["tableType"]?.let { configuration.tableType.value = TableType.valueOf(it) }
        parameters["crossBorder"]?.let { configuration.crossBorder.value = it.toBoolean() }
        parameters["zoneFilter"]?.let { configuration.zoneFilter.value = it }
        parameters["filter"]?.let { configuration.filter.value = it }
        parameters["zoomLevelLinear"]?.let { zoomLevelLinear.value = it.toDouble() }
        parameters["minZoomLevel"]?.let { minZoomLevel.value = it.toDouble() }
        parameters["maxZoomLevel"]?.let { maxZoomLevel.value = it.toDouble() }
        parameters["autoZoom"]?.let { autoZoom.value = it.toBoolean() }
        parameters["autoTerrain"]?.let { autoTerrain.value = it.toBoolean() }
        parameters["schematic"]?.let { schematic.value = it }
        parameters["cartographic"]?.let { cartographic.value = it.toDouble() }
        parameters["autoMorphing"]?.let { autoMorphing.value = it.toBoolean() }
        parameters["startZoomLevelMorphing"]?.let { startZoomLevelMorphing.value = it.toDouble() }
        parameters["endZoomLevelMorphing"]?.let { endZoomLevelMorphing.value = it.toDouble() }
        parameters["lineWidth"]?.let { lineWidth.value = it.toDouble() }
        parameters["fps"]?.let { fps.value = it.toDouble() }
        parameters["riskBuffer"]?.let { riskBuffer.value = it.toDouble() }
        parameters["riskThreshold"]?.let { riskThreshold.value = it.toDouble() }

        val updateCartographic = {
            val interpolate = zoomFactorInterpolate(
                threeDZoom.value,
                0.0,
                1.0,
                startZoomLevelMorphing.value,
                endZoomLevelMorphing.value
            )
            zoomLevelLinear.value = zoomLevelLinear.value
            if (autoMorphing.value) {
                //   val schematic = ((state.viewState.zoom as Double) - 8) * 3
                cartographic.value = interpolate * 10.0
            }
            lineWidth.value = zoomFactorInterpolate(threeDZoom.value, 2.0, 50.0, minZoomLevel.value, maxZoomLevel.value)
        }
        autoMorphing.addPropertyListener { updateCartographic() }
        threeDZoom.addPropertyListener { updateCartographic() }
        updateCartographic()

        val updateTerrain = {
            if (autoTerrain.value) {
                terrain.value = threeDZoom.value >= maxZoomLevel.value - 1.0
            }
        }
        autoTerrain.addPropertyListener { updateTerrain() }
        threeDZoom.addPropertyListener { updateTerrain() }

        diagramLoaders = arrayOf<ViewLoader>(
            ViewLoader(itcmDataFrame, logicalNetwork, diagramTypeProperty, parameters, masterDateTime, configuration.logicalBusProbing, configuration.logicalBranchProbing, configuration.logicalBusSelection, configuration.logicalBranchSelection, "/netzschema.svg", DiagramType.Tube),
            ViewLoader(itcmDataFrame, logicalNetwork, diagramTypeProperty, parameters, masterDateTime, configuration.logicalBusProbing, configuration.logicalBranchProbing, configuration.logicalBusSelection, configuration.logicalBranchSelection, "/geo.svg", DiagramType.Geo),
            ViewLoader(itcmDataFrame, logicalNetwork, diagramTypeProperty, parameters, masterDateTime, configuration.logicalBusProbing, configuration.logicalBranchProbing, configuration.logicalBusSelection, configuration.logicalBranchSelection, "/schematic.svg", DiagramType.Schematic)
        )

        parameters["playback"]?.let {
            if(it == "tube" || it == "schematic" || it == "geo") {
                diagramTypeProperty.value = if(it == "schematic") DiagramType.Schematic else if(it == "geo") DiagramType.Geo else DiagramType.Tube
                val viewLoader =  if(it == "schematic") diagramLoaders[2] else if(it == "geo") diagramLoaders[1] else diagramLoaders[0]

                println("Waiting for EFluxNetwork")
                viewLoader.efluxNetwork.wait {
                    val network = it!!
                    println("Waiting for SvgPanZoomContext")
                    viewLoader.svgPanZoomContext.wait {
                        val paletteType = PaletteType.dark

                        if (network != null) {
                            val colorNodes = colorNodes.value
                            val sizeNodes = sizeNodes.value

                            val thicknessLinks = thicknessLinks.value
                            val colorLinks = colorLinks.value
                            val movementLinks = movementLinks.value
                            val animationLinks = animationLinks.value

                            try {
                                updateDiagram(network, network.logical, colorNodes, sizeNodes, thicknessLinks, colorLinks, movementLinks, animationLinks, paletteType)
                            } catch (e: Exception) {
                                console.log(e)
                            }
                        }

                        println("Preparing animation")
                        val instance = it!!

                        labelSubstations.value = false

//                        val slauf1: EFluxNode = network.graphicalNodes[logicalBusIdOf("SLAUFE1")]!!
//                        val sbreit1 = network.graphicalNodes[logicalBusIdOf("SBREIT1")]!!

                        val timeline = timeline {
//                            parallel {
//                                zoom(it, initialScale, 10.0, 30000, Easing.LINEAR) {
//                                    duration = 30000
//                                }
//                            }
                            parallel {
                                zoomAtPoint(network.graphicalNodes[logicalBusIdOf("IGORM11")], it, 2.0, 10000, Easing.LINEAR)
                            }
                            parallel {
                                logicalBranchIdOf("IGORM11-SROBBI1-1").let { select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("D4TRSN1")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                zoom(it, 3.0, 10000, Easing.LINEAR)
                                onZoom(it, labelSubstations, 2.5, 10000)
                            }
                            parallel {
                                logicalBranchIdOf("D4KUHM1-SLAUFE1-2").let { select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("IGORM11")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                logicalBranchIdOf("SGOESG2-SMETTL2-1").let { select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("SLAVOR1")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                zoom(it, 5.0, 10000, Easing.LINEAR)
                            }
                            parallel {
                                logicalBranchIdOf("D4KUHM2-SLAUFE2-2").let {select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("SMETTL1")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                zoom(it, 7.0, 10000, Easing.LINEAR)
                            }
                            parallel {
                                logicalBranchIdOf("D4KUHM1-SLAUFE1-2").let {select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("SLAUFE1")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                logicalBranchIdOf("SGOESG2-SMETTL2-1").let {select(it, configuration.logicalBranchSelection, 10000) }
                                panTo(network.graphicalNodes[logicalBusIdOf("SMETTL2")], it, 10000, Easing.LINEAR)
                            }
                            parallel {
                                select(null, configuration.logicalBranchSelection, 10000)
                                logicalBusIdOf("SMETTL2").let {select(it, configuration.logicalBusSelection, 10000) }
                                zoom(it, 1.0, 10000, Easing.LINEAR)
                            }
//                            parallel {
//                                pan(slauf1, sbreit1, it, 10000, Easing.LINEAR)
//                            }
//                            parallel {
//                                zoom(it, it.getZoom(), 10.0, 10000, Easing.LINEAR)
//                            }
//                            parallel {
//                                pan(sbreit1, slauf1, it, 10000, Easing.LINEAR)
//                            }
//                            parallel {
//                                zoomAtPoint(sbreit1, it, 10.0, 30000, Easing.LINEAR)
//                                onZoom(it, localities, 7.0, 30000)
//                            }
//                            parallel {
//                                zoomAtPoint(slauf1, it, 10.0, 30000, Easing.LINEAR)
//                            }
                        }

                        playback.value = Playback(timeline, diagramTypeProperty.value)
                    }
                }
            } else if(it == "3d") {
                threeDPitch.value = 0.0

                diagramTypeProperty.value = DiagramType.ThreeD

                val timeline = timeline {
                    val duration: Long = 30000
                    parallel {
                        property(threeDZoom, 12.0, duration)
                        property(threeDLatitude, 47.3769, duration)
                        property(threeDLongitude, 8.5417, duration)
                        property(threeDPitch, 60.0, duration, Easing.QUAD_IN)
                    }
                    parallel {
                        property(threeDZoom, 10.0, duration)
                        property(threeDLatitude, (47.3769 + 47.3389) / 2, duration)
                        property(threeDLongitude, (8.5417 + 7.2427) / 2, duration)
                    }
                    parallel {
                        property(threeDZoom, 12.0, duration)
                        property(threeDLatitude, 47.3389, duration)
                        property(threeDLongitude, 7.2427, duration)
                    }
                }

                playback.value = Playback(timeline, DiagramType.ThreeD)
            } else {
                diagramLoaders[0].efluxNetwork.wait {
                    val network = it!!
                    diagramLoaders[0].svgPanZoomContext.wait {
                        val instance = it!!
                        playback(network, instance)
                    }
                }
            }
        }

        GlobalScope.launch {
            val dateAsString = parameters["date"]
            val earliest = earliest.await()
            val latest = latest.await()
            if (dateAsString != null) {
                if (dateAsString == "earliest") {
                    // Initialize date through synchronous call
                    configuration.timeTracker.dateInputted(earliest.toDateTime())
                } else if (dateAsString == "latest") {
                    configuration.timeTracker.dateInputted(latest.toDateTime(), false)
                } else {
                    configuration.timeTracker.dateInputted(dateAsString.toDateTime())
                }
            }

            var source = EventSource("/db/now")
            source.addEventListener(EventType("message"), {
                val value = it.asDynamic().data as String?
                if(value != null) {
                    configuration.nowDate.value = value
                }
            })

            startEFlux()
        }
    }

    private fun startEFlux() {
//        val adjustShiftedDateTime = {
//            when (lagUnit.value) {
//                LagUnit.minutes -> {
//                    shiftedDateTime.value = masterDateTime.value.plus(DateTimeSpan(minutes = shiftValue.value))
//                }
//                LagUnit.hours -> {
//                    shiftedDateTime.value = masterDateTime.value.plus(DateTimeSpan(hours = shiftValue.value))
//                }
//                LagUnit.days -> {
//                    shiftedDateTime.value = masterDateTime.value.plus(DateTimeSpan(days = shiftValue.value))
//                }
//                LagUnit.weeks -> {
//                    shiftedDateTime.value = masterDateTime.value.plus(DateTimeSpan(weeks = shiftValue.value))
//                }
//            }
//        }
//        adjustShiftedDateTime()
//
//        masterDateTime.addPropertyListener(object : PropertyListener<DateTime> {
//            override fun propertyChanged(event: PropertyEvent<DateTime>) {
//                println("Master date changed to $masterDateTime from ${event.oldValue}")
//                adjustShiftedDateTime.invoke()
//            }
//        })
//
//        shiftValue.addPropertyListener(object : PropertyListener<Int> {
//            override fun propertyChanged(event: PropertyEvent<Int>) {
//                adjustShiftedDateTime.invoke()
//            }
//        })
//
//        lagUnit.addPropertyListener(object : PropertyListener<LagUnit> {
//            override fun propertyChanged(event: PropertyEvent<LagUnit>) {
//                adjustShiftedDateTime.invoke()
//            }
//        })
//
//        shiftedDateTime.addPropertyListener(object : PropertyListener<DateTime> {
//            override fun propertyChanged(event: PropertyEvent<DateTime>) {
//                println("Shifted date changed to $shiftedDateTime from ${event.oldValue}")
//            }
//        })

        physicalNetwork.addPropertyListener(object : PropertyListener<PhysicalNetwork?> {
            override fun propertyChanged(event: PropertyEvent<PhysicalNetwork?>) {
                val newValue = event.newValue
                if (newValue != null) {
//                    val active = newValue.active
//                    println("Active network created")
                    val logical = newValue.logical
//                    println("Logical network created")
                    logicalNetwork.value = logical
//                    println("Logical network set to ${logicalNetwork.value}")
                } else {
                    println("Resetting logical network")
                    logicalNetwork.value = null
                }
            }
        })

        println("Downloading data")

        GlobalScope.launch {
            val coordinates =
                async {
                    loadCoordinates()
                }

            val itcm =
                async {
                    loadITCM()
                }

            masterDateTime.addPropertyListener(object : PropertyListener<DateTime> {
                override fun propertyChanged(event: PropertyEvent<DateTime>) {
                    println("Loading physical network at " + event.newValue.toRawString() + " != " + event.oldValue.toRawString() + ": " + (event.newValue == event.oldValue))
                    loadPhysicalNetwork(coordinates, itcm)
                }
            })
            loadPhysicalNetwork(coordinates, itcm)

            val eodSubstationDeferred = async { window.fetch("/db/eod").await().text().await() }
            logicalNetwork.addPropertyListener(object : PropertyListener<LogicalNetwork?> {
                override fun propertyChanged(event: PropertyEvent<LogicalNetwork?>) {
                    if(event.newValue != null) {
                        GlobalScope.launch {
                            val geometrySubstationDeferred = async { window.fetch("/db/substations/${masterDateTime.value.toRawString()}").await().text().await() }
                            val geometryLineDeferred = async { window.fetch("/db/lines/${masterDateTime.value.toRawString()}").await().text().await() }
                            val riskDeferred = async { window.fetch("/db/risks/${masterDateTime.value.toRawString()}").await().text().await() }
                            val weatherDeferred = async { window.fetch("/db/weather/${masterDateTime.value.toRawString()}").await().text().await() }

                            val geometrySubstationJson = geometrySubstationDeferred.await()
                            val eodSubstationDeferredJson = eodSubstationDeferred.await()
                            val geometryLineJson = geometryLineDeferred.await()
                            val riskLineJson = riskDeferred.await()
                            val weatherLineJson = weatherDeferred.await()

                            substationNetwork.value = swissgridNetwork(
                                masterDateTime.value.toRawString().toDateTimeTz(), physicalNetwork.value!!,
                                geometrySubstationJson, eodSubstationDeferredJson, geometryLineJson, riskLineJson, weatherLineJson
                            )
                        }
                    } else {
                        substationNetwork.value = null
                    }
                }
            })
        }

        run {
            val request = XMLHttpRequest()
            request.open("GET", "/koord_sub_20200729.json")
            request.onload = {
                if (request.readyState == 4.toShort() && request.status == 200.toShort()) {
                    println("Coordinates Loaded")
                    val dataFrame = DataFrame.readJson(request.responseText)
                    println("Coordinates Parsed")

                    //                dataFrame.printSchema()

                    coordinatesDataFrame.value = dataFrame
                } else {
                    coordinatesDataFrame.value = null
                }
            }
            request.send()
        }

        run {
            val request = XMLHttpRequest()
            request.open("GET", "/itcm.json")
            request.onload = {
                if (request.readyState == 4.toShort() && request.status == 200.toShort()) {
                    println("ICTM Loaded")
                    val dataFrame = DataFrame.readJson(request.responseText)
                    println("ICTM Parsed")

                    //                dataFrame.printSchema()

                    itcmDataFrame.value = dataFrame
                } else {
                    itcmDataFrame.value = null
                }
            }
            request.send()
        }

        run {
            val request = XMLHttpRequest()
            request.open("GET", "/amica.json")
            request.onload = {
                if (request.readyState == 4.toShort() && request.status == 200.toShort()) {
                    println("Amica Loaded")
                    val dataFrame = DataFrame.readJson(request.responseText)
                    println("Amica Parsed")

                    //                dataFrame.printSchema()

                    amicaDataFrame.value = dataFrame
                } else {
                    amicaDataFrame.value = null
                }
            }
            request.send()
        }

        parameters["animate"]?.let {
            val ms = it.toLong()
            println("Starting animation with 5 minutes = $ms ms")
            launch {
                while (true) {
                    delay(ms)
                    val current = masterDateTime.value.toJsDate()
                    val next = Date(current.getTime() + 1000 * 60 * 5)
                    println("Next date: " + next.toRawString())
                    masterDateTime.value = next.toDateTime()
                }
            }
        }
    }

    private fun playback(network: EFluxNetwork, instance: SvgPanZoomContext) {
        val ms = 40L
        launch {
//            val link = network.graphicalLinks[logicalBranchIdOf("SBASSE1-SLAUFE1-1")]!!
//            pathSelection.setSelectedElement(link)
            labelSubstations.value = false

            val slauf1 = network.graphicalNodes[logicalBusIdOf("SLAUFE1")]!!

            val initialScale = instance.getZoom()
            var zoom = initialScale

            val timer = object : Timer() {
                override fun run() {
                    if (zoom < 12) {
                        val slauf1Point = slauf1.svgToScreen(slauf1.anchorSVG)
                        val nodeX = slauf1Point.x
                        val nodeY = slauf1Point.y

//                println("$nodeX,$nodeY")
                        zoom = zoom * 1.005
                        if(zoom > 2.0) {
                            labelSubstations.value = true
                        }
                        instance.zoomAtPoint(zoom, object {
                            val x = nodeX
                            val y = nodeY
                        }, true)

                    } else {
                        cancel()
                    }
                }
            }
            timer.scheduleRepeating(1000)
            println("Timer scheduled...")

//            delay(1000L)

//            animationLinks.value = false
//            movementLinks.value = null
//            backgroundLinks.value = null
//            masterDateTime.value = "20220311_0715".toDateTime()
//            shiftUnit.value = ShiftUnit.minutes
//            shiftValue.value = -1440

//            delay(1000L)

//            delay(1000L)
//            movementLinks.value = LinkField.pratea

//            delay(1000L)
//            animationLinks.value = true

//            delay(1000L)
//            thicknessLinks.value = LinkField.pratea

//            delay(1000L)
//            backgroundNodes.value = NodeField.vm

//            delay(1000L)
//            backgroundNodes.value = null
//            backgroundLinks.value = LinkField.dva

//            while (zoom < 12) {
//                delay(ms)
////                val bbox = node.elements.first().getBBox()
////                val nodeX = bbox.x + (bbox.width / 2)
////                val nodeY = bbox.y + (bbox.height / 2)
//
////                val nodeX = node.screenX
////                val nodeY = node.screenY
//
//                val slauf1Point = slauf1.svgToScreen(slauf1.anchorSVG)
//                val nodeX = slauf1Point.x
//                val nodeY = slauf1Point.y
//
////                println("$nodeX,$nodeY")
//                zoom = zoom * 1.005
//                if(zoom > 2.0) {
//                    localities.value = true
//                }
//                instance.zoomAtPoint(zoom, object {
//                    val x = nodeX
//                    val y = nodeY
//                }, true)
//
//            }
//
////            var duration = 0.0
////            while (duration < 1.0) {
////                delay(ms)
////
////                val slauf1Point = slauf1.svgToScreen(slauf1.anchorSVG)
////                val sbreit1Point = sbreit1.svgToScreen(sbreit1.anchorSVG)
////
////                val nodeX = (slauf1Point.x * (1.0 - duration)) + (sbreit1Point.x * duration)
////                val nodeY = (slauf1Point.y * (1.0 - duration)) + (sbreit1Point.y * duration)
////
////                println("Move $nodeX,$nodeY")
////
////                instance.pan(object {
////                    val x = nodeX
////                    val y = nodeY
////                })
////
////                duration += 0.003
////            }
//
//            var duration = 0.0
//            val slauf1Point = slauf1.svgToScreen(slauf1.anchorSVG)
//            val sbreit1Point = sbreit1.svgToScreen(sbreit1.anchorSVG)
//
//            val panX = (sbreit1Point.x - slauf1Point.x) / 1000.0
//            val panY = (sbreit1Point.y - slauf1Point.y) / 1000.0
//
//            while (duration < 1.0) {
//                delay(ms)
//
//                instance.panBy(object {
//                    val x = -panX
//                    val y = -panY
//                })
//
//                duration += 0.001
//            }
//
//            delay(3000L)
//            diagramTypeProperty.value = DiagramType.Schematic
//
//            delay(5000L)
//            diagramTypeProperty.value = DiagramType.ThreeD
        }
    }

    @OptIn(ExperimentalTime::class)
    private fun loadPhysicalNetwork(coordinates: Deferred<DataFrame<Int,String,Any?>>, itcm: Deferred<DataFrame<Int,String,Any?>>) {
        val raw = false

        if (raw) {
            println("Loading physical network at " + masterDateTime.value)
            val request = XMLHttpRequest()
            //    val rawUrl = "raw/S1_M03_" + parameters.get("date") + "_CHSNP_NW_MODEL_000.raw"
            val rawUrl = "/raw/" + masterDateTime.value
            request.open("GET", rawUrl)
            request.onload = {
                if (request.readyState == 4.toShort() && request.status == 200.toShort()) {
                    println("Raw Loaded")
                    val parser = RawPowerFlowParser(request.responseText, masterDateTime.value.unixMillisLong)
                    println("Raw Parsed")

                    val physical = PhysicalNetwork(parser)
                    println("Physical network created")
                    physicalNetwork.value = physical
                } else {
                    physicalNetwork.value = null
                }
            }
            request.send()
        } else {
            GlobalScope.launch {
                val date = masterDateTime.value.toRawString()

                val physical: PhysicalNetwork
                with(measureTime {
                    physical = loadPhysicalNetworkFromDatabase(date, coordinates, itcm)
                }) {
                    println("Physical network created in $this")
                }

                physicalNetwork.value =  physical
            }
        }
    }

    val currentViewLoader: ViewLoader?
        get() {
            when(diagramTypeProperty.value) {
                DiagramType.Tube -> { return diagramLoaders[0] }
                DiagramType.Geo -> { return diagramLoaders[1] }
                DiagramType.Schematic -> { return diagramLoaders[2] }
                else -> { return null }
            }
        }

    fun zoomOut() {
        val envelope = Envelope(5.9559, 10.4921, 45.8179, 47.8085)
        val zoom = calculateZoomLevel(envelope.minX, envelope.minY, envelope.maxX, envelope.maxY, window.innerWidth, window.innerHeight, 0.07)
        threeDZoom.value = zoom
        val centroid = envelope.centre()!!
        threeDLatitude.value = centroid.y
        threeDLongitude.value = centroid.x
    }

    fun logicalZoom(geometry: Geometry?, margin: Double = 0.05) {
        if(autoZoom.value) {
            zoom(geometry, margin, startZoomLevelMorphing.value)
        }
    }

    fun physicalZoom(geometry: Geometry?, margin: Double = 0.05) {
        if(autoZoom.value) {
            if(geometry is org.locationtech.jts.geom.Point) {
                zoom(geometry, margin, maxZoomLevel.value - 1.0)
            } else {
                zoom(geometry, margin)
            }
        }
    }

    fun isMoreCartographic(): Boolean {
        return cartographic.value >= 5.0
    }

    fun overviewZoom(logicalGeometry: Geometry?, physicalGeometry: Geometry?, margin: Double = 0.05) {
//        if(autoZoom.value) {
//            val isMoreCartographic = isMoreCartographic()
//
            if(!autoMorphing.value) {
//                if(physicalGeometry is org.locationtech.jts.geom.Point) {
//                    zoom(physicalGeometry, margin, startZoomLevelMorphing.value)
//                } else {
//                    zoom(physicalGeometry, margin, startZoomLevelMorphing.value)
//                }
                zoom(physicalGeometry, margin, endZoomLevelMorphing.value)
            } else {
                zoom(logicalGeometry, margin, startZoomLevelMorphing.value)
            }
//        }
    }

    fun autoZoom(geometry: Geometry?, margin: Double = 0.05) {
        if(autoZoom.value) {
            val isCartographic = cartographic.value >= 10.0
            val isMoreCartographic = isMoreCartographic()
            val isMoreZoomed = threeDZoom.value > (startZoomLevelMorphing.value + endZoomLevelMorphing.value) / 2.0
            val isCartographicZoomed = threeDZoom.value >= endZoomLevelMorphing.value && isCartographic

            if(isCartographicZoomed) {
                if(geometry is org.locationtech.jts.geom.Point) {
                    zoom(geometry, margin, maxZoomLevel.value - 1.0)
                } else {
                    zoom(geometry, margin)
                }
            } else {
                zoom(geometry, margin, startZoomLevelMorphing.value)
            }
        }
    }

    fun forceZoom(geometry: Geometry?, margin: Double = 0.05) {
        if(geometry is org.locationtech.jts.geom.Point) {
            zoom(geometry, margin, maxZoomLevel.value - 1.0)
        } else {
            zoom(geometry, margin)
        }
    }

    private fun zoom(geometry: Geometry?, margin: Double, zoomLevel: Double? = null) {
        val physicalGeometry = geometry
        if (physicalGeometry != null) {
            val envelope = physicalGeometry.envelopeInternal
            val zoom = zoomLevel ?: calculateZoomLevel(
                envelope.minX,
                envelope.minY,
                envelope.maxX,
                envelope.maxY,
                window.innerWidth,
                window.innerHeight,
                margin
            )

            threeDZoom.value = zoom
            val centroid = physicalGeometry.centroid
            threeDLatitude.value = centroid.y
            threeDLongitude.value = centroid.x
        }
    }

    fun getColor(d: Line, theme: Theme) : MkColor {
        val name = d.longName
        val c = if (name == null) (theme.asDynamic().invalidColor as MkColor)
        else {
            val dp = d.dp
            if(dp != null && dp.amount.absoluteValue == 0.0) (theme.asDynamic().outOfServiceColor as MkColor)
            else if (name.contains("380 kV")) (theme.asDynamic().kv380Color as MkColor)
            else if (name.contains("220 kV")) (theme.asDynamic().kv220Color as MkColor)
            else if (name.contains("150 kV")) (theme.asDynamic().kv150Color as MkColor)
            else (theme.asDynamic().invalidColor as MkColor)
        }
        return c
    }

    val red = colorOf(228, 3, 46).toKotlinJS() // #e4032e
    val orange = colorOf(242, 130, 0).toKotlinJS() // #f28200
    val yellow = colorOf(255, 204, 0).toKotlinJS() // #ffcc00
    val green = colorOf(23, 175, 151).toKotlinJS() // #17af97

    val severe = colorOf(139, 0, 0)
    val serious = colorOf(255, 69, 0)
    val warning = colorOf(255, 215, 0)
    val normal = colorOf(0, 128, 0)

    fun getColor(d: Coordinate, theme: Theme) : Color {
        val fill = (theme.asDynamic().substationFillColor as MkColor).toKotlinJS()
        return if(d.probability != null) {
            if (d.probability >= 0.1) severe.toKotlinJS()
            else if (d.probability >= 0.05) serious.toKotlinJS()
            else if (d.probability > 0.0) warning.toKotlinJS()
            else fill
        } else {
            fill
        }
    }

    fun getColor(d: Substation, theme: Theme) : Color {
        val fill = (theme.asDynamic().substationFillColor as MkColor).toKotlinJS()

        val baskv = d.baskv
        val c = if(d.type == "real") {
            val vm = d.vm
            val voltage = if(baskv == null || vm == null) null else baskv.amount * vm.amount
            when {
                voltage == null -> (theme.asDynamic().invalidColor as MkColor).toKotlinJS()
                voltage == 0.0 -> (theme.asDynamic().outOfService as MkColor).toKotlinJS()
                voltage > 421.0 -> red
                voltage > 418.0 -> orange
                voltage > 416.0 -> yellow
                voltage > 399.0 -> fill
                voltage > 397.0 -> yellow
                voltage > 390.0 -> orange
                voltage > 360.0 -> red
                voltage > 246.0 -> red
                voltage > 243.0 -> orange
                voltage > 241.0 -> yellow
                voltage > 229.0 -> fill
                voltage > 227.0 -> yellow
                voltage > 220.0 -> orange
                voltage > 210.0 -> red
                else -> fill
            }
        }
        else if(baskv == 380.Kilovolt)
            (theme.asDynamic().kv380Color as MkColor).toKotlinJS()
        else if(baskv == 220.Kilovolt)
            (theme.asDynamic().kv220Color as MkColor).toKotlinJS()
        else if(baskv == 150.Kilovolt)
            (theme.asDynamic().kv150Color as MkColor).toKotlinJS()
        else
            (theme.asDynamic().invalidColor as MkColor).toKotlinJS()
        return c
    }
}
